import type Luminary from '@lumen5/luminary';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { VideoPlan } from 'js/src/client';
import type {
    VideoDraft,
    VideoDraftCreationTool,
} from 'js/src/data/video-planner/video-planner-types';
import { isOnMobileDevice } from 'js/src/libs/mobile-check';

export interface VideoPlannerState {
    videoDrafts: VideoDraft[];
    selectedVideoDraft: string | null;
}

const initialState: VideoPlannerState = {
    videoDrafts: [],
    selectedVideoDraft: null,
};

export const videoPlannerSlice = createSlice({
    name: 'videoPlanner',
    initialState,
    reducers: {
        addVideoDraft: (
            state,
            action: PayloadAction<{
                id: string;
                luminary: Luminary;
                name: string;
                estimatedLoadingDurationSeconds: number;
                creationToolName: VideoDraftCreationTool;
                videoPlan?: VideoPlan;
                brandKitId?: number | null;
            }>,
        ) => {
            const newDraft: VideoDraft = {
                id: action.payload.id,
                luminary: action.payload.luminary,
                name: action.payload.name,
                error_message: null,
                preview_canvas: null,
                isGenerating: true,
                estimatedLoadingDurationSeconds: action.payload.estimatedLoadingDurationSeconds,
                creationToolName: action.payload.creationToolName,
                videoPlan: action.payload.videoPlan,
                brandKitId: action.payload.brandKitId,
            };

            state.videoDrafts.push(newDraft as any);

            // Auto-select the newly created video draft if not on mobile
            if (!isOnMobileDevice()) {
                state.selectedVideoDraft = newDraft.id;
            }
        },
        setVideoDraftError: (
            state,
            action: PayloadAction<{
                id: string;
                error_message: string;
            }>,
        ) => {
            const draft = state.videoDrafts.find((d) => d.id === action.payload.id);
            if (draft) {
                draft.error_message = action.payload.error_message;
            }
        },
        setVideoDraftPreviewCanvas: (
            state,
            action: PayloadAction<{
                id: string;
                canvas: HTMLCanvasElement;
            }>,
        ) => {
            const draft = state.videoDrafts.find((d) => d.id === action.payload.id);
            if (draft) {
                draft.preview_canvas = action.payload.canvas as any;
            }
        },
        setVideoDraftGenerating: (
            state,
            action: PayloadAction<{
                id: string;
                isGenerating: boolean;
            }>,
        ) => {
            const draft = state.videoDrafts.find((d) => d.id === action.payload.id);
            if (draft) {
                draft.isGenerating = action.payload.isGenerating;
            }
        },
        setSelectedVideoDraft: (state, action: PayloadAction<string | null>) => {
            state.selectedVideoDraft = action.payload;
        },
        updateVideoDraftFeedback: (
            state,
            action: PayloadAction<{
                videoDraftId: string;
                feedbackRating: 1 | 5;
            }>,
        ) => {
            const draft = state.videoDrafts.find((d) => d.id === action.payload.videoDraftId);
            if (draft) {
                draft.feedbackRating = action.payload.feedbackRating;
            }
        },
        setVideoDraftSavedVideoId: (
            state,
            action: PayloadAction<{
                id: string;
                savedVideoId: number;
                savedVideoDataHash: string;
            }>,
        ) => {
            const draft = state.videoDrafts.find((d) => d.id === action.payload.id);
            if (draft) {
                draft.savedVideoId = action.payload.savedVideoId;
                draft.savedVideoDataHash = action.payload.savedVideoDataHash;
            }
        },
        clearAllVideoDrafts: (state) => {
            state.videoDrafts.forEach((draft) => {
                draft.luminary?.destroy();
            });
            state.videoDrafts = [];
            state.selectedVideoDraft = null;
        },
    },
});

export const {
    addVideoDraft,
    setVideoDraftError,
    setVideoDraftPreviewCanvas,
    setVideoDraftGenerating,
    setSelectedVideoDraft,
    updateVideoDraftFeedback,
    setVideoDraftSavedVideoId,
    clearAllVideoDrafts,
} = videoPlannerSlice.actions;

export const videoPlannerReducer = videoPlannerSlice.reducer;
